<template lang="pug">
.comments
    .comments-title
        h2 {{$t(`${baseTranslationPath}.comments_title`)}}

    .field-container.full-width.is-required.comment-field
        .el-input
            .custom-rich-text-container
                RichText(@update="onUpdateRichTextComment('comment', null, $event)"
                :placeHolder="$t(`${baseTranslationPath}.drop_a_comment_placeholder`)"
                :inChat="false")
        .comment-btn
            aiq-button( @click="clearComment('comment')") {{ $t('global.cancel') }}
            aiq-button(type="primary" @click="addComment(null, 'comment')") {{ $t('global.save') }}
    .comment-section(v-for="comment in comments" :key="comment.id")
            .comment
                .comment-header-wrapper
                    .comment-header.pad-top
                        aiq-image(:src="avatar(comment)" :key="avatar(comment)")
                    .comment-name.pad-item
                        .name {{ name(comment) }}
                    .comment-time.pad-item
                        aiq-timeago.time-ago(:since="comment.created_at" :minTime="0")
                    .comment-rejected.pad-item
                        span {{ DocumentRejected(comment) }}
                .comment-body-wrapper
                    .comment-body(v-html="comment.content")
                .comment-footer-wrapper
                    .comment-reply
                        .comment-icon(@click="toggleReplyField(comment)")
                            fa-icon(:icon="['fas', 'reply']" class="reply-icon")
                            span.icon-text {{ $t(`${baseTranslationPath}.reply_comment`) }}

                        .comment-icon(v-if="canDelete(comment)" @click="deleteComment(comment)")
                            fa-icon(:icon="['fas', 'trash-can']")
                            span.icon-text {{ $t(`global.delete`) }}
            .reply-field(v-if="comment.showReplyField")
                .field-container.full-width.is-required.comment-field.reply-comment-field
                    .el-input
                        .custom-rich-text-container
                            RichText(@update="onUpdateRichTextComment('reply', comment, $event)"
                                    :placeHolder="$t(`${baseTranslationPath}.drop_a_reply_placeholder`)"
                                    :inChat="false")
                .comment-btn
                    aiq-button( @click="toggleReplyField(comment)") {{ $t('global.cancel') }}
                    aiq-button(type="primary" @click="addComment(comment, 'reply', replies[comment.id])") {{ $t('global.save') }}
            .reply-section(v-for="reply in comment.replies" :key="reply.id")
                .comment
                    .comment-header-wrapper
                        .comment-header.pad-top
                            aiq-image(:src="avatar(reply)" :key="avatar(reply)")
                        .comment-name.pad-item
                            .name {{ name(reply) }}
                        .comment-time.pad-item
                            aiq-timeago.time-ago(:since="reply.created_at" :minTime="0")
                .comment-body-wrapper
                    .comment-body(v-html="reply.content")
                .comment-footer-wrapper
                    .comment-reply
                        .comment-icon(v-if="canDelete(reply)" @click="deleteComment(reply)")
                            fa-icon(:icon="['fas', 'trash-can']")
                            span.icon-text {{ $t(`global.delete`) }}
</template>
<script>
import RichText from '@/components/RichText/RichText.vue';
import mitt from 'mitt';
import { defaultProfile } from '@/libs';
import get from 'lodash/get';
import { mapState } from 'vuex';

export default {
  name: 'CommentNew',
  components: {
    RichText,
  },
  data() {
    return {
      baseTranslationPath:
        'settings_tab.admin_tab.smart_control.control_list_manage',
      newComment: '',
      replies: {},
      richTextEventEmitter: mitt(),
    };
  },
  props: {
    comments: {
      type: Array,
      default: () => [],
    },
    documentId: {
      type: String,
      default: null,
    },
  },
  provide() {
    return {
      richTextEventEmitter: this.richTextEventEmitter,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.richTextEventEmitter.emit('setText', '');
    });
  },
  computed: {
    ...mapState({
      agent: (state) => state.agent.profile,
    }),
  },
  emits: ['addComment', 'deleteComment'],
  methods: {
    avatar(comment) {
      return get(comment, 'agent.picture.payload.url', defaultProfile.picture);
    },
    onUpdateRichTextComment(type, parent_comment, value) {
      if (type === 'comment') {
        this.newComment = get(value, 'html', '');
      } else if (type === 'reply') {
        this.replies[parent_comment.id] = get(value, 'html', '');
      }
    },
    clearComment(type, parent_comment) {
      if (type === 'comment') {
        this.newComment = '';
        this.richTextEventEmitter.emit('setText', '');
      } else if (type === 'reply') {
        this.replies[parent_comment.id] = '';
      }
    },
    toggleReplyField(comment) {
      comment.showReplyField = !comment.showReplyField;
    },

    name(comment) {
      const first_name = get(
        comment,
        'agent.profile.first_name',
        defaultProfile.first_name,
      );
      const last_name = get(
        comment,
        'agent.profile.last_name',
        defaultProfile.last_name,
      );
      return `${first_name} ${last_name}`.trim();
    },
    DocumentRejected(comment) {
      const reject = get(comment, 'payload.type') === 'reject';
      if (reject) {
        return this.$t(`${this.baseTranslationPath}.reject`);
      }
      return '';
    },
    canDelete(comment) {
      const agent_id = get(comment, 'agent_id');
      const logged_in_agent_id = get(this.agent, 'id');
      const isRejection = get(comment, 'payload.type') === 'reject';
      const hasPermission = this.canView(
        '/settings/smartdrive/smart_control/comment/delete',
      );
      return agent_id === logged_in_agent_id && !isRejection && hasPermission;
    },
    addComment(comment, type, reply = '') {
      if (type === 'reply') {
        this.replies[comment.id] = '';
        const parent_id = get(comment, 'id');
        this.$emit('addComment', {
          id: this.documentId,
          comment: reply,
          parent_id,
        });
      } else if (type === 'comment') {
        this.$emit('addComment', {
          id: this.documentId,
          comment: this.newComment,
        });
        this.newComment = '';
        this.richTextEventEmitter.emit('setText', '');
      }
    },
    async confirmDelete() {
      try {
        await this.$aiq.confirm(
          this.$t(`${this.baseTranslationPath}.delete_confirm.title`),
          this.$t(`${this.baseTranslationPath}.delete_confirm.message`),
        );
        return true;
      } catch {
        return false;
      }
    },
    async deleteComment(comment) {
      if (!comment || !(await this.confirmDelete())) {
        return;
      }
      this.$emit('deleteComment', {
        id: this.documentId,
        comment_id: comment.id,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import "src/styles/aiq-variables.scss";
.comment {
  padding-bottom: 20px;
}
.comments{
    margin-bottom: 150px;
}
.reply-comment-field{
    margin-left:70px
}
.comment-field{
    padding-top:15px;
    padding-bottom:15px;
}
.comment-btn{
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
    margin-top: 20px;
}

.comment-header-wrapper {
  display: flex;
  align-items: flex-start;
}
.comment-rejected {
  color: red;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  padding-top:7px;
}
.pad-item {
  padding-left: 7px;
  padding-right: 7px;
}

.icon-text {
    color: #6a7682;
    padding-left: 5px;
}
.comment-time {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    padding-top:3px;
    color: #6a7682;
    padding-top:7px;
}
.comment-name {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    padding-top:4px;
}

img {
      width: 30px;
      height: 30px;
      border-radius: 50%;
}
.comment-body {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
}
.comment-body-wrapper {
    padding-top: 10px;
    padding-right: 30px;
    padding-bottom: 10px;
    padding-left: 35px;
    color: #6a7682;
}
.comment-reply{
    display: flex;
}
.reply-icon {
    color: $aiq-color-active;
}
.comment-icon {
    color: #6a7682;
    font-size: 14px;
    cursor: pointer;
    padding-left: 10px;
    padding-right: 10px;
}
.comment-footer-wrapper {
    padding-left: 30px;
    padding-top: 5px;
    padding-bottom: 5px;
}
.reply-section {
    margin-left: 40px;
    margin-top: 10px;
}

:deep(.ProseMirror) {
    min-height: 120px;
    max-height: 180px;
}

:deep(ul) {
  margin: unset;
  padding: unset;
  padding-left: 1.5rem;
  li {
    list-style: disc;
    list-style-type: initial;
    margin: unset;
    padding: unset;
  }
}
:deep(ol) {
  margin: unset;
  padding: unset;
  padding-left: 1.5rem;
  li {
    list-style: decimal;
    margin: unset;
    padding: unset;
  }
}
</style>
